// Insurance-line model (spec Section 1) — the four lines a subscriber can run.
//
// A subscriber picks one, several, or all. This is the source of truth for
// per-line configuration (hiding irrelevant tools, per-line pipelines, etc.).
// Kept as a pure, dependency-free module so the selection + legacy-mapping logic
// is unit-testable and reusable by both the onboarding step and Settings.
//
// Backward-compat: the CRM already routes off a coarse `workspaceMode`
// (life | health | both). We DERIVE that from the granular lines so existing
// routing keeps working while we migrate surfaces to the four-line model — we
// never break the working CRM to add the new concept.

'use strict';

const INSURANCE_LINES = [
  { id: 'life',           label: 'Life Insurance',          blurb: 'Final expense, term, whole, IUL, mortgage protection' },
  { id: 'aca',            label: 'ACA / Marketplace Health', blurb: 'Marketplace eligibility, SEP, subsidies, enrollment' },
  { id: 'private_health', label: 'Private Health',           blurb: 'Short-term, indemnity, supplemental, dental & vision' },
  { id: 'medicare',       label: 'Medicare',                 blurb: 'Advantage, Supplement, Part D, SOA' },
];

const LINE_IDS = INSURANCE_LINES.map((l) => l.id);
const HEALTH_LINE_IDS = ['aca', 'private_health', 'medicare'];

// Validate + dedupe an incoming selection down to known line ids, preserving
// the canonical order. Anything unrecognized is dropped (never fabricated).
function normalizeLines(input) {
  const set = new Set(Array.isArray(input) ? input : []);
  return LINE_IDS.filter((id) => set.has(id));
}

function hasLine(lines, id) {
  return normalizeLines(lines).includes(id);
}

// Derive the legacy workspaceMode (life | health | both) from the granular
// lines, so existing routing (pickCockpitRoutes) keeps working. Default when
// nothing is selected is 'both' — show everything, never hide the whole CRM.
function linesToWorkspaceMode(lines) {
  const l = normalizeLines(lines);
  const hasLife = l.includes('life');
  const hasHealth = l.some((x) => HEALTH_LINE_IDS.includes(x));
  if (hasLife && hasHealth) return 'both';
  if (hasLife) return 'life';
  if (hasHealth) return 'health';
  return 'both';
}

// Human summary for confirmation UI / audit ("Life + Medicare").
function linesSummary(lines) {
  const l = normalizeLines(lines);
  if (!l.length) return 'No lines selected';
  return l.map((id) => INSURANCE_LINES.find((x) => x.id === id).label).join(' + ');
}

module.exports = {
  INSURANCE_LINES,
  LINE_IDS,
  HEALTH_LINE_IDS,
  normalizeLines,
  hasLine,
  linesToWorkspaceMode,
  linesSummary,
};
